/**
 * Отчёты (/admin/reports/clinics…) — показатели клиник платформы: свод
 * с выбором клиники → детальные отчёты (сводка, выручка, врачи, услуги).
 * Оркестратор держит период (пресеты BE) и выбранную клинику.
 */

import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {ArrowLeft} from 'lucide-react';

import {getClinicsReport, type ReportPeriod} from '@services/reports';
import {keys} from '@queries/keys';
import {Loader} from '@components/ui/Loader';
import {cn} from '@lib/utils';
import {formatNumber} from '@utils/format';

import {ClinicReportDetail} from './ClinicReportDetail';
import {ClinicsReportTable} from './ClinicsReportTable';
import {REPORT_PERIODS} from './Reports.utils';

export const Reports = () => {
  const [period, setPeriod] = useState<ReportPeriod>('1m');
  const [clinicId, setClinicId] = useState<string | null>(null);

  const {data, isLoading} = useQuery({
    queryKey: keys.reports.clinics(period),
    queryFn: () => getClinicsReport(period),
  });

  const clinic = clinicId === null ? null : data?.rows.find((r) => r.clinicId === clinicId);

  return (
    <>
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur max-md:px-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {clinicId !== null && (
            <button
              onClick={() => setClinicId(null)}
              aria-label="К своду"
              className="grid size-9 shrink-0 place-items-center rounded-[9px] border border-input bg-card text-muted-foreground"
            >
              <ArrowLeft className="size-[17px]" strokeWidth={2} />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="truncate text-[21px] font-bold -tracking-[.02em]">
              {clinic?.name ?? 'Отчёты'}
            </h1>
            <div className="mt-0.5 text-[13px] text-muted-foreground">
              {clinic
                ? `${clinic.cityName} · отчёты клиники`
                : 'Показатели клиник платформы · только клиники'}
            </div>
          </div>
        </div>
        <div className="flex rounded-[10px] border border-skr-line bg-card p-[3px]">
          {REPORT_PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={cn(
                'rounded-[8px] px-3 py-1.5 text-[12.5px] font-medium text-muted-foreground transition',
                period === p.key && 'bg-skr-head font-semibold text-foreground',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-[1080px] px-[30px] pb-[60px] pt-[22px] max-md:px-4">
        {clinicId !== null ? (
          <ClinicReportDetail clinicId={clinicId} period={period} />
        ) : isLoading || !data ? (
          <div className="flex min-h-[440px] items-center justify-center">
            <Loader label="Загружаем свод…" />
          </div>
        ) : data.rows.length === 0 ? (
          <div className="flex min-h-[300px] items-center justify-center text-[13.5px] text-muted-foreground">
            Клиник с активностью за период нет
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 text-[13px] text-muted-foreground">
              <span>
                Выручка нетто:{' '}
                <b className="font-mono text-foreground">{formatNumber(data.totalRevenueNet)}</b>{' '}
                сом
              </span>
              <span>
                Долги пациентов:{' '}
                <b className="font-mono text-foreground">{formatNumber(data.totalDebt)}</b> сом
              </span>
            </div>
            <div className="overflow-hidden rounded-[13px] border border-skr-line bg-card">
              <ClinicsReportTable rows={data.rows} onOpen={setClinicId} />
            </div>
          </>
        )}
      </div>
    </>
  );
};
